import React, { useState, useMemo, useEffect } from 'react';
import { ICONS } from '../constants';
import { useTranslation } from '../hooks/useTranslation';
import Modal from './ui/Modal';
import { API_CONFIG } from '../config';
import { Artist } from '../types';

interface User {
  id: string;
  name: string;
  email?: string;
  specialization?: string;
}

interface AddUsersToProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  users: User[]; // All available users/artists
  assignedUserIds?: string[]; // Already assigned user IDs (to exclude or mark)
  onAddUsers: (projectId: string, userIds: string[]) => Promise<void>;
}

const AddUsersToProjectModal: React.FC<AddUsersToProjectModalProps> = ({
  isOpen,
  onClose,
  projectId,
  projectName,
  users,
  assignedUserIds = [],
  onAddUsers
}) => {
  const { t } = useTranslation();
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [assignedUserIdsState, setAssignedUserIdsState] = useState<string[]>(assignedUserIds || []);
  const [loadingAssigned, setLoadingAssigned] = useState(false);

  // Fetch assigned users when modal opens
  useEffect(() => {
    if (isOpen && projectId) {
      const fetchAssignedUsers = async () => {
        setLoadingAssigned(true);
        try {
          const token = localStorage.getItem('spellbook_token');
          const response = await fetch(`${API_CONFIG.API_URL}/projects/${projectId}/artists`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          
          if (response.ok) {
            const data = await response.json();
            const assigned = Array.isArray(data) ? data : (data.artists || []);
            const assignedIds = assigned.map((a: any) => a.id || a.artistId);
            setAssignedUserIdsState(assignedIds);
          }
        } catch (error) {
          console.error('Failed to fetch assigned users:', error);
        } finally {
          setLoadingAssigned(false);
        }
      };
      
      fetchAssignedUsers();
    } else {
      // Reset when modal closes
      setAssignedUserIdsState(assignedUserIds || []);
      setSelectedUserIds(new Set());
      setSearchQuery('');
    }
  }, [isOpen, projectId, assignedUserIds]);

  // Filter users: exclude already assigned ones and filter by search
  const availableUsers = useMemo(() => {
    return users.filter(user => {
      // Exclude telegram_bot (additional safety check)
      if (user.name === 'telegram_bot' || user.email?.includes('telegram_bot')) {
        return false;
      }
      // Exclude already assigned users
      if (assignedUserIdsState.includes(user.id)) {
        return false;
      }
      // Filter by search query
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        return (
          user.name.toLowerCase().includes(query) ||
          user.email?.toLowerCase().includes(query) ||
          user.specialization?.toLowerCase().includes(query)
        );
      }
      return true;
    });
  }, [users, assignedUserIdsState, searchQuery]);

  const handleToggleUser = (userId: string) => {
    setSelectedUserIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedUserIds.size === availableUsers.length) {
      setSelectedUserIds(new Set());
    } else {
      setSelectedUserIds(new Set(availableUsers.map(u => u.id)));
    }
  };

  const handleSave = async () => {
    if (selectedUserIds.size === 0) {
      alert(t('addUsersToProject.selectUsers') || 'Please select at least one user');
      return;
    }

    setIsSaving(true);
    try {
      await onAddUsers(projectId, Array.from(selectedUserIds));
      setSelectedUserIds(new Set());
      setSearchQuery('');
      onClose();
    } catch (error) {
      console.error('Failed to add users to project:', error);
      alert(t('addUsersToProject.error') || 'Failed to add users to project');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setSelectedUserIds(new Set());
    setSearchQuery('');
    onClose();
  };


  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('addUsersToProject.title') || 'Add Users to Project'}
      size="lg"
    >
      <div className="space-y-4">
        {/* Project Info */}
        <div className="p-3 bg-indigo-500/10 border border-indigo-500/50 rounded-lg">
          <div className="flex items-center gap-2">
            <ICONS.Briefcase className="w-5 h-5 text-indigo-400" />
            <span className="text-white font-semibold">{projectName}</span>
          </div>
        </div>

        {/* Search */}
        <div>
          <input
            type="text"
            placeholder={t('addUsersToProject.searchPlaceholder') || 'Search by name, email, or specialization...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500"
          />
        </div>

        {/* Select All */}
        {availableUsers.length > 0 && (
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={selectedUserIds.size === availableUsers.length && availableUsers.length > 0}
                onChange={handleSelectAll}
                className="w-4 h-4 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-300">
                {t('addUsersToProject.selectAll') || 'Select All'} ({availableUsers.length})
              </span>
            </label>
            <span className="text-sm text-gray-400">
              {selectedUserIds.size} {t('addUsersToProject.selected') || 'selected'}
            </span>
          </div>
        )}

        {/* Users List */}
        <div className="max-h-96 overflow-y-auto border border-gray-700 rounded-lg">
            {loadingAssigned ? (
              <div className="p-8 text-center text-gray-400">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-400 mx-auto mb-2"></div>
                <p>{t('common.loading') || 'Loading...'}</p>
              </div>
            ) : availableUsers.length === 0 ? (
              <div className="p-8 text-center text-gray-400">
                <ICONS.User className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>{t('addUsersToProject.noUsers') || 'No available users'}</p>
                {assignedUserIdsState.length > 0 && (
                  <p className="text-xs mt-2 text-gray-500">
                    {assignedUserIdsState.length} {t('addUsersToProject.alreadyAssigned') || 'users already assigned'}
                  </p>
                )}
              </div>
          ) : (
            <div className="divide-y divide-gray-700">
              {availableUsers.map((user) => (
                <label
                  key={user.id}
                  className="flex items-center gap-3 p-3 hover:bg-gray-800 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={selectedUserIds.has(user.id)}
                    onChange={() => handleToggleUser(user.id)}
                    className="w-4 h-4 text-indigo-600 bg-gray-700 border-gray-600 rounded focus:ring-indigo-500"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-medium">{user.name}</div>
                    {user.email && (
                      <div className="text-sm text-gray-400">{user.email}</div>
                    )}
                    {user.specialization && (
                      <div className="text-xs text-gray-500 mt-1">
                        {user.specialization}
                      </div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
          <button
            onClick={handleClose}
            disabled={isSaving}
            className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition disabled:opacity-50"
          >
            {t('common.cancel') || 'Cancel'}
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || selectedUserIds.size === 0}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition disabled:opacity-50 flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                <span>{t('common.saving') || 'Saving...'}</span>
              </>
            ) : (
              <>
                <ICONS.Plus className="w-4 h-4" />
                <span>
                  {t('addUsersToProject.add') || 'Add'} ({selectedUserIds.size})
                </span>
              </>
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default AddUsersToProjectModal;

